import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import {
    Box,
    Button,
    Card,
    Chip,
    Container,
    Divider,
    Grid,
    InputAdornment,
    Skeleton,
    Typography,
    Tooltip,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    Link,
} from "@mui/material"
import {
    AccountBalance,
    FileDownload,
    FilterList,
    Search,
    TrendingUp,
    TrendingDown,
} from "@mui/icons-material"
import { DesktopDatePicker } from "@mui/x-date-pickers"
import { Helmet } from "react-helmet"
import moment from "moment"
import { api } from "../../../services/Main/Api"
import { getCondoId } from "../../../services/Main/AuthStorage"
import Context from "../../../contexts/Context"
import KPICard from "../Dashboard/components/KPICard"
import NoResultsAnimation from "../../../components/Shared/animations/NoResultsAnimation"
import InvoiceDetail from "../Invoice/InvoiceDetail"
import TransferDetail from "../Rent/RecipientTransferHistory/TransferDetail"
import PayoutDetailDialog from "./components/PayoutDetailDialog"
import ChargeDetailDialog from "./components/ChargeDetailDialog"
import PaymentDetailDialog from "./components/PaymentDetailDialog"
import CustomerInvoiceDetailDialog from "./components/CustomerInvoiceDetailDialog"

// -------- helpers --------
const currencyBRL = (n) =>
    (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })

const ptWeek = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"]
const weekdayDateLabel = (d) => {
    const dd = String(d.getDate()).padStart(2, "0")
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const yyyy = d.getFullYear()
    return `${ptWeek[d.getDay()]}, ${dd}/${mm}/${yyyy}`
}

const localDayKey = (d) => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    return `${y}-${m}-${day}`
}
const localDateFromKey = (key) => {
    const [y, m, d] = key.split("-").map(Number)
    return new Date(y, m - 1, d, 0, 0, 0)
}

const fmtDate = (d) => {
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const dd = String(d.getDate()).padStart(2, "0")
    return `${yyyy}-${mm}-${dd}`
}

function mapBankEntry(item) {
    const id = item.id ?? item.entry_id ?? item.EntryId ?? item.EntryID ?? `${Math.random()}`

    const dateRaw =
        item.entry_date ??
        item.date ??
        item.transactionDate ??
        item.created_at ??
        item.Date ??
        item.PostingDate

    let iso = dateRaw
    try {
        const d = new Date(dateRaw)
        if (!isNaN(d.getTime())) iso = d.toISOString()
    } catch (e) {}

    const desc =
        item.details ??
        item.description ??
        item.history ??
        item.comment ??
        item.counterPartyName ??
        "Transação"

    const nature = (item.nature ?? item.type ?? item.Nature ?? item.Type ?? "")
        .toString()
        .toUpperCase()
    const isCredit = nature.includes("CREDIT") || nature.includes("CRÉDITO")
    const isDebit = nature.includes("DEBIT") || nature.includes("DÉBITO")

    let v = item.entry_value ?? item.value ?? item.amount ?? item.Amount ?? 0
    v = Number(v) || 0

    if ("creditValue" in item && Number(item.creditValue)) v = Number(item.creditValue)
    if ("debitValue" in item && Number(item.debitValue)) v = -Math.abs(Number(item.debitValue))
    if (!("debitValue" in item) && !("creditValue" in item) && (isCredit || isDebit)) {
        v = Math.abs(v) * (isDebit ? -1 : 1)
    }
    if (Number.isInteger(v) && Math.abs(v) > 100000 && Math.abs(v) % 10 === 0) {
        v = v / 100
    }

    const metadata = item.metadata ?? null

    const managedAccountId =
        item.managed_account ??
        metadata?.managed_account ??
        null

    return { id, data: iso, descricao: desc, valor: v, metadata, managedAccountId }
}

const META_KIND_CONFIG = {
    invoice:        { label: 'Fatura' },
    payout:         { label: 'Repasse' },
    charge:         { label: 'Cobrança' },
    money_transfer: { label: 'Transferência TED' },
    pix_transfer:   { label: 'Transferência PIX' },
    payment:        { label: 'Pagamento' },
    CustomerInvoice: { label: 'Tarifa' },
}

const getEntryMeta = (row) => {
    const md = row?.metadata
    const config = META_KIND_CONFIG[md?.kind]
    if (!md || !config) return null
    const identifier = md.identifier ?? md.id ?? null
    if (!identifier) return null
    return {
        kind: md.kind,
        id: identifier,
        number: md.number ?? "",
        label: config.label,
    }
}

const getTags = (row) => {
    const mdTags = row?.metadata?.tags
    const directTags = row?.tags
    const list = Array.isArray(mdTags) ? mdTags : Array.isArray(directTags) ? directTags : []
    const normalized = list
        .map((t) => (t == null ? "" : String(t)).trim())
        .filter(Boolean)

    const seen = new Set()
    const uniq = []
    for (const t of normalized) {
        const key = t.toLowerCase()
        if (seen.has(key)) continue
        seen.add(key)
        uniq.push(t)
    }
    return uniq
}

// -------- filter type chips --------
const TYPE_OPTIONS = [
    { key: null, label: 'Todos', color: '#64748B' },
    { key: 'credit', label: 'Entradas', color: '#22C55E' },
    { key: 'debit', label: 'Saídas', color: '#EF4444' },
    { key: 'invoice', label: 'Fatura', color: '#3B82F6' },
    { key: 'payout', label: 'Repasse', color: '#8B5CF6' },
    { key: 'charge', label: 'Cobrança', color: '#F59E0B' },
    { key: 'payment', label: 'Pagamento', color: '#06B6D4' },
    { key: 'CustomerInvoice', label: 'Tarifa', color: '#EC4899' },
    { key: 'money_transfer', label: 'TED', color: '#14B8A6' },
    { key: 'pix_transfer', label: 'PIX', color: '#10B981' },
]

export default function NewBankStatement() {
    const { filters, setFilters } = useContext(Context)
    const effectiveCondoId = getCondoId()
    const typingTimerRef = useRef(null)

    // date range
    const [startDate, setStartDate] = useState(moment().startOf('month').toDate())
    const [endDate, setEndDate] = useState(moment().endOf('month').toDate())
    const [dateFrom, setDateFrom] = useState(moment().startOf('month').format('YYYY-MM-DD'))
    const [dateTo, setDateTo] = useState(moment().endOf('month').format('YYYY-MM-DD'))

    // filters
    const [search, setSearch] = useState("")
    const [activeType, setActiveType] = useState(null)

    const [rows, setRows] = useState([])
    const [balanceByDay, setBalanceByDay] = useState({})
    const [summary, setSummary] = useState(null)
    const [page, setPage] = useState(1)
    const [nextPage, setNextPage] = useState(null)
    const [hasMore, setHasMore] = useState(false)
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [err, setErr] = useState("")

    // export modal
    const [exportOpen, setExportOpen] = useState(false)
    const [exportMonth, setExportMonth] = useState(() => {
        const now = new Date()
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
    })
    const [exportType, setExportType] = useState("ofx")
    const [exporting, setExporting] = useState(false)

    // managed bank accounts lookup: { [id]: account }
    const [accountsMap, setAccountsMap] = useState({})

    // detail modals
    const [payoutModal, setPayoutModal] = useState({ open: false, id: null })
    const [chargeModal, setChargeModal] = useState({ open: false, id: null })
    const [paymentModal, setPaymentModal] = useState({ open: false, id: null })
    const [customerInvoiceModal, setCustomerInvoiceModal] = useState({ open: false, id: null })
    const [transferOpen, setTransferOpen] = useState(false)
    const [transferFilters, setTransferFilters] = useState({})

    const handleOpenDetail = (kind, id) => {
        switch (kind) {
            case 'invoice':
                setFilters(prev => ({ ...prev, modalInvoice: true, invoiceId: id }))
                break
            case 'payout':
                setPayoutModal({ open: true, id })
                break
            case 'charge':
                setChargeModal({ open: true, id })
                break
            case 'money_transfer':
                setTransferFilters({ transferType: 'ted', id, load: true })
                setTransferOpen(true)
                break
            case 'pix_transfer':
                setTransferFilters({ transferType: 'pix', id, load: true })
                setTransferOpen(true)
                break
            case 'payment':
                setPaymentModal({ open: true, id })
                break
            case 'CustomerInvoice':
                setCustomerInvoiceModal({ open: true, id })
                break
            default:
                break
        }
    }

    const fetchEntriesPage = async (df, dt, pageNum = 1) => {
        const res = await api.get("/fitbank/bank-statement-entry/", {
            params: { condo_id: effectiveCondoId, date_from: df, date_to: dt, page: pageNum },
        })

        const dataContainer = res.data || {}
        const nextNum = dataContainer?.next_number ?? null
        const results = Array.isArray(dataContainer?.results)
            ? dataContainer.results
            : Array.isArray(dataContainer)
                ? dataContainer
                : []

        return {
            entries: results.map(mapBankEntry),
            nextNum,
            hasMore: Boolean(dataContainer?.next),
        }
    }

    const fetchDailyBalances = async (df, dt) => {
        const res = await api.get("/fitbank/bank-final-balance/", {
            params: {
                condo_id: effectiveCondoId,
                date_from: df,
                date_to: dt,
                page_size: 400,
            },
        })

        const data = res.data ?? {}
        const list = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : []
        const dict = {}

        for (const item of list) {
            const dateRaw = item.entry_date || item.date || item.day
            const d = new Date(dateRaw)
            const key = localDayKey(d)

            const balance = Number(item.entry_value ?? 0) || 0
            const total_in = Number(item.total_in ?? 0) || 0
            const total_out = Math.abs(Number(item.total_out ?? 0) || 0)

            dict[key] = { balance, total_in, total_out }
        }

        return dict
    }

    useEffect(() => {
        let alive = true
        setErr("")
        setLoading(true)
        setRows([])
        setPage(1)
        setNextPage(null)
        setHasMore(false)

        ;(async () => {
            try {
                const [page1, balances, summaryRes, accountsRes] = await Promise.all([
                    fetchEntriesPage(dateFrom, dateTo, 1),
                    fetchDailyBalances(dateFrom, dateTo),
                    api.post("analytics/bank-statement-summary/", {
                        condo_id: effectiveCondoId,
                        date_from: dateFrom,
                        date_to: dateTo,
                    }).catch(() => ({ data: null })),
                    api.get("cashflow/managed-bank-accounts/", {
                        params: { condo_id: effectiveCondoId },
                    }).catch(() => ({ data: null })),
                ])
                if (!alive) return

                setRows(page1.entries)
                setBalanceByDay(balances)
                setPage(1)
                setNextPage(page1.nextNum)
                setHasMore(page1.hasMore)
                if (summaryRes.data) setSummary(summaryRes.data)
                if (accountsRes.data) {
                    const list = accountsRes.data?.results || accountsRes.data || []
                    const map = {}
                    for (const acc of list) {
                        if (acc.id) map[acc.id] = acc
                    }
                    setAccountsMap(map)
                }
            } catch (e) {
                console.error(e)
                if (!alive) return
                setErr("Não foi possível carregar o extrato/saldos. Verifique a API ou os filtros.")
                setRows([])
                setBalanceByDay({})
                setNextPage(null)
                setHasMore(false)
            } finally {
                if (alive) setLoading(false)
            }
        })()

        return () => {
            alive = false
        }
    }, [dateFrom, dateTo, effectiveCondoId])

    const handleLoadMore = async () => {
        if (!hasMore || !nextPage) return
        setLoadingMore(true)
        try {
            const resp = await fetchEntriesPage(dateFrom, dateTo, nextPage)
            setRows((prev) => {
                const seen = new Set(prev.map((r) => r.id))
                const merged = [...prev]
                for (const e of resp.entries) {
                    if (!seen.has(e.id)) merged.push(e)
                }
                return merged
            })
            setPage(nextPage)
            setNextPage(resp.nextNum)
            setHasMore(resp.hasMore)
        } catch (e) {
            console.error(e)
            setErr("Falha ao carregar mais transações.")
        } finally {
            setLoadingMore(false)
        }
    }

    const kpiData = {
        saldoAtual: summary?.saldo_atual ?? 0,
        entradas: summary?.total_in ?? 0,
        saidas: summary?.total_out ?? 0,
    }

    // -------- client-side filtering (search + type) --------
    const filteredRows = useMemo(() => {
        let result = rows

        if (search) {
            const term = search.toLowerCase()
            result = result.filter((r) => r.descricao.toLowerCase().includes(term))
        }

        if (activeType === 'credit') {
            result = result.filter((r) => r.valor > 0)
        } else if (activeType === 'debit') {
            result = result.filter((r) => r.valor < 0)
        } else if (activeType) {
            result = result.filter((r) => r.metadata?.kind === activeType)
        }

        return result
    }, [rows, search, activeType])

    const sorted = useMemo(() => [...filteredRows].sort((a, b) => new Date(b.data) - new Date(a.data)), [filteredRows])

    const groupedByDay = useMemo(() => {
        const grouped = {}
        for (const r of sorted) {
            const d = new Date(r.data)
            const key = localDayKey(d)
            if (!grouped[key]) grouped[key] = []
            grouped[key].push({
                ...r,
                _dateObj: d,
                timeLabel: d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
                isNegative: Number(r.valor) < 0,
            })
        }
        Object.values(grouped).forEach((list) => list.sort((a, b) => b._dateObj - a._dateObj))
        return grouped
    }, [sorted])

    const dayKeys = useMemo(() => {
        return Object.keys(groupedByDay).sort((a, b) => {
            const da = localDateFromKey(a)
            const db = localDateFromKey(b)
            return db - da
        })
    }, [groupedByDay])

    const downloadBlob = (blob, filename) => {
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = filename
        a.click()
        URL.revokeObjectURL(url)
    }

    const handleExportConfirm = async () => {
        setExporting(true)
        try {
            if (exportType === "ofx") {
                const [y, m] = exportMonth.split("-").map(Number)
                const first = new Date(y, m - 1, 1)
                const last = new Date(y, m, 0)
                const df = fmtDate(first)
                const dt = fmtDate(last)

                const res = await api.get("/fitbank/export-ofx/", {
                    params: {
                        condo_id: effectiveCondoId,
                        date_from: df,
                        date_to: dt,
                    },
                    responseType: "blob",
                })

                const cd = res.headers?.["content-disposition"]
                const match = cd && /filename="?([^"]+)"?/.exec(cd)
                const fallbackName = `extrato_${effectiveCondoId}_${df}_a_${dt}.ofx`
                const filename = match ? match[1] : fallbackName

                const blob = new Blob([res.data], { type: "application/x-ofx" })
                downloadBlob(blob, filename)
                setExportOpen(false)
            } else {
                const res = await api.get("/fitbank/export-csv/", {
                    params: {
                        condo_id: effectiveCondoId,
                        month: exportMonth.split("-")[1],
                        year: exportMonth.split("-")[0],
                    },
                    responseType: "blob",
                })

                const cd = res.headers?.["content-disposition"]
                const match = cd && /filename="?([^"]+)"?/.exec(cd)
                const fallbackName = `extrato_${effectiveCondoId}_${exportMonth}.csv`
                const filename = match ? match[1] : fallbackName

                const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" })
                downloadBlob(blob, filename)
                setExportOpen(false)
            }
        } catch (e) {
            console.error(e)
            setErr("Falha ao exportar. Verifique o endpoint e os parâmetros.")
        } finally {
            setExporting(false)
        }
    }

    return (
        <>
            <Helmet>
                <title>Extrato Bancário - Bloco B3</title>
            </Helmet>
            <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>
                <Container maxWidth="xl" sx={{ py: 3 }}>
                    {/* Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 3,
                            flexWrap: 'wrap',
                            gap: 2,
                        }}
                    >
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B' }}>
                            Extrato Bancário
                        </Typography>
                        <Tooltip title="Exportar extrato por mês">
                            <span>
                                <Button
                                    variant="outlined"
                                    startIcon={<FileDownload />}
                                    onClick={() => {
                                        setExportMonth(moment(startDate).format('YYYY-MM'))
                                        setExportOpen(true)
                                    }}
                                    disabled={loading}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        borderColor: '#E2E8F0',
                                        color: '#64748B',
                                        '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },
                                    }}
                                >
                                    Exportar
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>

                    {/* KPI Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={4}>
                            <KPICard
                                title="Saldo Atual"
                                value={kpiData.saldoAtual}
                                variation={0}
                                variationLabel=""
                                icon={<AccountBalance />}
                                color="#3B82F6"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <KPICard
                                title="Entradas"
                                value={kpiData.entradas}
                                variation={0}
                                variationLabel=""
                                icon={<TrendingUp />}
                                color="#22C55E"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <KPICard
                                title="Saídas"
                                value={kpiData.saidas}
                                variation={0}
                                variationLabel=""
                                icon={<TrendingDown />}
                                color="#EF4444"
                            />
                        </Grid>
                    </Grid>

                    {/* Filters Card */}
                    <Card
                        sx={{
                            border: '1px solid #E2E8F0',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            mb: 2,
                            borderRadius: 2,
                        }}
                    >
                        <Box sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <FilterList sx={{ color: '#94A3B8', fontSize: 20 }} />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#94A3B8',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        fontSize: '0.7rem',
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    Filtros
                                </Typography>
                            </Box>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        placeholder="Buscar por descrição..."
                                        variant="outlined"
                                        size="small"
                                        onKeyUp={(event) => {
                                            clearTimeout(typingTimerRef.current)
                                            typingTimerRef.current = setTimeout(
                                                () => setSearch(event.target.value),
                                                800
                                            )
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search sx={{ color: '#94A3B8', fontSize: 20 }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                bgcolor: '#fff',
                                                '& fieldset': { borderColor: '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={2.5}>
                                    <DesktopDatePicker
                                        label="De"
                                        value={startDate}
                                        onChange={(val) => {
                                            if (!val) return
                                            setStartDate(val)
                                            setDateFrom(moment(val).format('YYYY-MM-DD'))
                                        }}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                inputProps: { readOnly: true },
                                                sx: {
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        bgcolor: '#fff',
                                                        '& fieldset': { borderColor: '#E2E8F0' },
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={2.5}>
                                    <DesktopDatePicker
                                        label="Até"
                                        value={endDate}
                                        onChange={(val) => {
                                            if (!val) return
                                            setEndDate(val)
                                            setDateTo(moment(val).format('YYYY-MM-DD'))
                                        }}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                inputProps: { readOnly: true },
                                                sx: {
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        bgcolor: '#fff',
                                                        '& fieldset': { borderColor: '#E2E8F0' },
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            {/* Type Chips */}
                            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                                {TYPE_OPTIONS.map((opt) => (
                                    <Chip
                                        key={opt.key ?? 'all'}
                                        label={opt.label}
                                        size="small"
                                        onClick={() => setActiveType(opt.key)}
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            cursor: 'pointer',
                                            borderRadius: '8px',
                                            height: 32,
                                            px: 0.5,
                                            bgcolor: activeType === opt.key ? `${opt.color}14` : 'transparent',
                                            color: activeType === opt.key ? opt.color : '#64748B',
                                            border: activeType === opt.key
                                                ? `1.5px solid ${opt.color}`
                                                : '1.5px solid #E2E8F0',
                                            '&:hover': {
                                                bgcolor: `${opt.color}14`,
                                                borderColor: opt.color,
                                            },
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Card>

                    {err && (
                        <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>
                    )}

                    {/* Statement Content */}
                    {loading ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Skeleton variant="rounded" height={120} animation="pulse" />
                            <Skeleton variant="rounded" height={200} animation="pulse" />
                            <Skeleton variant="rounded" height={200} animation="pulse" />
                        </Box>
                    ) : dayKeys.length === 0 ? (
                        <Card
                            sx={{
                                border: '1px solid #E2E8F0',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                borderRadius: 2,
                                py: 4,
                            }}
                        >
                            <NoResultsAnimation />
                        </Card>
                    ) : (
                        <>
                            {dayKeys.map((key) => {
                                const d = localDateFromKey(key)
                                const header = weekdayDateLabel(d)
                                const apiTotals = balanceByDay[key]
                                const dayRows = groupedByDay[key] || []

                                const computed = dayRows.reduce(
                                    (acc, r) => {
                                        if (r.valor > 0) acc.in += r.valor
                                        if (r.valor < 0) acc.out += Math.abs(r.valor)
                                        return acc
                                    },
                                    { in: 0, out: 0 }
                                )

                                const totalIn = apiTotals?.total_in ?? computed.in
                                const totalOutAbs = apiTotals?.total_out ?? computed.out
                                const saldoDia =
                                    apiTotals && typeof apiTotals.balance === "number"
                                        ? currencyBRL(apiTotals.balance)
                                        : "—"

                                return (
                                    <Card
                                        key={key}
                                        sx={{
                                            mb: 2,
                                            border: '1px solid #E2E8F0',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                            borderRadius: 2,
                                            overflow: "hidden",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                px: 2.5,
                                                py: 1.5,
                                                bgcolor: "#F8FAFC",
                                                borderBottom: "1px solid #E2E8F0",
                                                gap: 2,
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: 600, color: '#1E293B' }}
                                            >
                                                {header}
                                            </Typography>

                                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                <Tooltip title="Total de entradas do dia">
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                        <TrendingUp sx={{ fontSize: 18, color: '#22C55E' }} />
                                                        <Typography variant="subtitle2" sx={{ color: '#22C55E', fontWeight: 600 }}>
                                                            {currencyBRL(totalIn)}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>

                                                <Tooltip title="Total de saídas do dia">
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                        <TrendingDown sx={{ fontSize: 18, color: '#EF4444' }} />
                                                        <Typography variant="subtitle2" sx={{ color: '#EF4444', fontWeight: 600 }}>
                                                            {currencyBRL(totalOutAbs)}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>

                                                <Divider orientation="vertical" flexItem />

                                                <Typography variant="subtitle2" sx={{ color: '#64748B', fontWeight: 600 }}>
                                                    Saldo {saldoDia}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box>
                                            {dayRows.map((row, idx) => {
                                                const entryMeta = getEntryMeta(row)
                                                const tags = getTags(row)
                                                const linkedAccount = row.managedAccountId
                                                    ? typeof row.managedAccountId === 'number'
                                                        ? accountsMap[row.managedAccountId]
                                                        : null
                                                    : null
                                                const bankLabel = typeof row.managedAccountId === 'string'
                                                    ? row.managedAccountId
                                                    : linkedAccount?.nickname || linkedAccount?.bank_name || null

                                                return (
                                                    <Box
                                                        key={row.id}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            px: 2.5,
                                                            py: 1.25,
                                                            gap: 2,
                                                            transition: 'background-color 0.15s ease',
                                                            '&:hover': { bgcolor: '#F8FAFC' },
                                                            ...(idx < dayRows.length - 1 && {
                                                                borderBottom: '1px solid #F1F5F9',
                                                            }),
                                                        }}
                                                    >
                                                        {/* Indicator dot */}
                                                        <Box
                                                            sx={{
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius: '50%',
                                                                bgcolor: row.isNegative ? '#EF4444' : '#22C55E',
                                                                flexShrink: 0,
                                                            }}
                                                        />

                                                        {/* Time */}
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: '#94A3B8',
                                                                minWidth: 40,
                                                                flexShrink: 0,
                                                                fontSize: '0.75rem',
                                                            }}
                                                        >
                                                            {row.timeLabel}
                                                        </Typography>

                                                        {/* Description + meta link + tags */}
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                {bankLabel && (
                                                                    <Tooltip title={bankLabel} arrow placement="top">
                                                                        <AccountBalance
                                                                            sx={{
                                                                                fontSize: 14,
                                                                                color: linkedAccount?.color || '#94A3B8',
                                                                                flexShrink: 0,
                                                                                cursor: 'default',
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                )}
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        color: '#1E293B',
                                                                        fontWeight: 400,
                                                                        fontSize: '0.85rem',
                                                                        lineHeight: 1.4,
                                                                    }}
                                                                    noWrap
                                                                >
                                                                    {row.descricao}
                                                                </Typography>

                                                                {entryMeta && (
                                                                    <Link
                                                                        component="button"
                                                                        underline="none"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation()
                                                                            handleOpenDetail(entryMeta.kind, entryMeta.id)
                                                                        }}
                                                                        sx={{
                                                                            color: '#3B82F6',
                                                                            fontSize: '0.75rem',
                                                                            fontWeight: 500,
                                                                            cursor: 'pointer',
                                                                            bgcolor: '#3B82F60A',
                                                                            px: 0.75,
                                                                            py: 0.25,
                                                                            borderRadius: 1,
                                                                            '&:hover': { bgcolor: '#3B82F614' },
                                                                            flexShrink: 0,
                                                                        }}
                                                                    >
                                                                        {entryMeta.number || entryMeta.label}
                                                                    </Link>
                                                                )}



                                                                {tags.map((t) => (
                                                                    <Typography
                                                                        key={`${row.id}-${t}`}
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: '#94A3B8',
                                                                            fontSize: '0.7rem',
                                                                            bgcolor: '#F1F5F9',
                                                                            px: 0.75,
                                                                            py: 0.125,
                                                                            borderRadius: 0.75,
                                                                            flexShrink: 0,
                                                                        }}
                                                                    >
                                                                        {t}
                                                                    </Typography>
                                                                ))}
                                                            </Box>
                                                        </Box>

                                                        {/* Value */}
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: row.isNegative ? '#EF4444' : '#22C55E',
                                                                fontWeight: 600,
                                                                fontSize: '0.85rem',
                                                                flexShrink: 0,
                                                                textAlign: 'right',
                                                                minWidth: 100,
                                                            }}
                                                        >
                                                            {currencyBRL(row.valor)}
                                                        </Typography>
                                                    </Box>
                                                )
                                            })}
                                        </Box>
                                    </Card>
                                )
                            })}

                            {hasMore && (
                                <Box mt={2} display="flex" justifyContent="center">
                                    <Button
                                        variant="outlined"
                                        onClick={handleLoadMore}
                                        disabled={loadingMore}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderRadius: 2,
                                            borderColor: '#E2E8F0',
                                            color: '#64748B',
                                            '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },
                                        }}
                                    >
                                        {loadingMore ? "Carregando..." : "Carregar mais"}
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </Container>
            </Box>

            {/* Modal de Exportação */}
            <Dialog open={exportOpen} onClose={() => !exporting && setExportOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Exportar extrato</DialogTitle>
                <DialogContent dividers>
                    <Box display="grid" gap={2} mt={0.5}>
                        <TextField
                            label="Mês"
                            type="month"
                            size="small"
                            value={exportMonth}
                            onChange={(e) => setExportMonth(e.target.value)}
                            inputProps={{ max: "9999-12" }}
                        />

                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Tipo de arquivo
                            </Typography>
                            <RadioGroup row value={exportType} onChange={(e) => setExportType(e.target.value)}>
                                <FormControlLabel value="ofx" control={<Radio />} label="OFX" />
                                <FormControlLabel value="csv" control={<Radio />} label="CSV" />
                            </RadioGroup>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExportOpen(false)} disabled={exporting}>
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={handleExportConfirm} disabled={exporting || !exportMonth}>
                        {exporting ? "Exportando..." : "Exportar"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal Fatura */}
            {filters?.modalInvoice && <InvoiceDetail key={filters?.invoiceId} />}

            {/* Modal Repasse */}
            <PayoutDetailDialog
                open={payoutModal.open}
                transferId={payoutModal.id}
                onClose={() => setPayoutModal({ open: false, id: null })}
            />

            {/* Modal Cobrança */}
            <ChargeDetailDialog
                open={chargeModal.open}
                chargeId={chargeModal.id}
                onClose={() => setChargeModal({ open: false, id: null })}
            />

            {/* Modal Transferência TED/PIX */}
            <TransferDetail
                transferOpen={transferOpen}
                setTransferOpen={setTransferOpen}
                filters={transferFilters}
                setFilters={setTransferFilters}
            />

            {/* Modal Pagamento */}
            <PaymentDetailDialog
                open={paymentModal.open}
                paymentId={paymentModal.id}
                onClose={() => setPaymentModal({ open: false, id: null })}
            />

            {/* Modal Tarifa */}
            <CustomerInvoiceDetailDialog
                open={customerInvoiceModal.open}
                customerInvoiceId={customerInvoiceModal.id}
                onClose={() => setCustomerInvoiceModal({ open: false, id: null })}
            />
        </>
    )
}
